import { useEffect, useState } from "react";
import { Archive, ExternalLink, Newspaper } from "lucide-react";
import api from "../lib/api";
import { Spinner } from "./common";

const CATEGORY_LABELS = {
  travel: "Top Stories", destinations: "Destinations", aviation: "Airlines & Airports",
  hotels: "Hotels & Resorts", cruise: "Cruise", impacting: "Impacting Travel",
  deals: "Deals", general: "General", airline: "Airline", airport: "Airport",
};

/* Permanent news archive — every headline the site has ever fetched, deduped by
   link. Dead links are purged nightly; everything else is kept for reference. */
export default function NewsArchiveFeed({ q = "" }) {
  const [cat, setCat] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);   // {items, total, pages, categories}
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setPage(1); }, [q, cat]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const p = new URLSearchParams({ page: String(page), limit: "24" });
    if (q) p.set("q", q);
    if (cat) p.set("category", cat);
    api.get(`/news/archive?${p.toString()}`).then((r) => {
      if (!alive) return;
      setData(r.data);
      setItems((prev) => (page === 1 ? r.data.items : [...prev, ...r.data.items]));
    }).catch(() => { if (alive) setData({ items: [], total: 0, pages: 1, categories: [] }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [q, cat, page]);

  if (!data) return <Spinner />;

  return (
    <div data-testid="news-archive">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <span className="inline-flex items-center gap-1.5 text-sm font-700 text-zinc-700">
          <Archive className="w-4 h-4 text-blue-700" /> {data.total.toLocaleString()} archived stories
        </span>
        <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="archive-category-select"
          className="border border-zinc-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:border-blue-600">
          <option value="">All categories</option>
          {(data.categories || []).map((c) => (
            <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>
          ))}
        </select>
        <span className="text-xs text-muted-foreground">Dead links are checked nightly and removed automatically.</span>
      </div>

      {items.length === 0 && !loading ? (
        <p className="text-sm text-muted-foreground">No archived stories match yet — browse the news tabs and every headline gets archived automatically.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {items.map((n) => (
            <a key={n.link} href={n.link} target="_blank" rel="noreferrer" data-testid={`archive-item-${n.id}`}
              onClick={() => api.post("/news/click", { link: n.link }).catch(() => {})}
              className="group block border border-zinc-300 bg-white p-4 hover:-translate-y-px hover:border-primary transition-[transform,border-color]">
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wide text-primary mb-1">
                <Newspaper className="w-3 h-3" />{n.source}
                <span className="text-muted-foreground normal-case tracking-normal">
                  · {n.published ? new Date(n.published).toLocaleDateString() : new Date(n.archived_at).toLocaleDateString()}
                </span>
                {n.category && <span className="ml-auto border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-[10px] text-zinc-600 normal-case tracking-normal">{CATEGORY_LABELS[n.category] || n.category}</span>}
              </div>
              <div className="font-heading font-700 text-base leading-snug group-hover:text-primary transition-colors">{n.title}</div>
              {n.snippet && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{n.snippet}</p>}
              <div className="mt-2 inline-flex items-center gap-1 text-xs text-primary">Read at {n.source} <ExternalLink className="w-3 h-3" /></div>
            </a>
          ))}
        </div>
      )}

      {loading && <div className="mt-4"><Spinner /></div>}
      {!loading && page < data.pages && (
        <button onClick={() => setPage((p) => p + 1)} data-testid="archive-load-more"
          className="mt-6 px-5 py-2.5 border border-blue-600 text-blue-700 text-sm font-700 hover:bg-blue-50 transition-colors">
          Load more ({data.total - items.length} remaining)
        </button>
      )}
    </div>
  );
}
